/**
 * 締結後の契約更新（amendment）の差分を人が読める形に整えるユーティリティ。
 *
 * BE は変更のあったフィールドだけを `{ api_field: { previous, new } }` で返す。
 * 値は JSON 化された正規形（金額は税抜の文字列、日付は ISO 文字列、対価行は
 * 配列）なので、表示側でフィールドごとに整形し直す必要がある。
 *
 * タイムラインのシステムメッセージ（`SystemMessageContent`）と、契約カードの
 * 承認待ち表示（`ContractAmendmentPanel`）、提案モーダルの確認ステップで共用する。
 */
import type {
  ApiCompensationLine,
  DmAmendableField,
  DmAmendmentChange,
  DmPendingAmendment,
} from "../app/services/dmThreads";
import { formatOfferAmountWithTax, formatOfferDateTime } from "./offerFormatters";

/** 値が無いときの表示（OfferSummaryCard と表現を揃える）。 */
const EMPTY_DASH = "—";

/**
 * API フィールド名 → 画面表示ラベル。BE の `AMENDABLE_API_FIELDS` と 1:1。
 *
 * ラベルはオファー作成フォーム（`steps/DetailFormStep` / `ContractAmendModal`）の
 * 表記に揃える。同じ項目が入力欄と差分表示で別の呼び名になると、何が変わったのか
 * 追いにくくなるため。
 */
export const AMENDMENT_FIELD_LABELS: Record<DmAmendableField, string> = {
  assigned_roles: "ロール名",
  scope: "スコープ",
  initial_message: "メッセージ",
  // 契約金額は内訳（`compensation_lines`）から同期されるので、どちらの経路でも
  // ユーザーから見れば「対価」1 項目。
  contract_amount: "対価",
  compensation_lines: "対価",
  contract_start_date: "契約開始日",
  contract_end_date: "契約終了日",
  deadline: "納期",
  quantity: "数量",
  deliverable_format: "納品形式",
  delivery_method: "取引方法",
  delivery_location: "引渡場所",
  special_provisions: "特記事項",
};

/**
 * 差分に出す順番。BE の dict 順はリクエストのキー順に依存するので、
 * 表示側で「契約書の読み順」に固定する。
 */
const FIELD_ORDER: DmAmendableField[] = [
  "assigned_roles",
  "deliverable_format",
  "deadline",
  "contract_amount",
  "compensation_lines",
  "special_provisions",
  "scope",
  "initial_message",
  "contract_start_date",
  "contract_end_date",
  "delivery_method",
  "delivery_location",
  "quantity",
];

const isCompensationLines = (
  value: DmAmendmentChange["previous"],
): value is ApiCompensationLine[] => Array.isArray(value);

/**
 * 対価行の**税抜**小計（整数円）。BE の
 * `OfferCreator.sync_amount_from_compensation_lines` と同じ規則で、これが
 * `contract_amount` に同期される。
 */
const sumApiCompensationLines = (lines: ApiCompensationLine[]): number => {
  const subtotal = lines.reduce((total, line) => {
    const unit = parseFloat(String(line.unit_price ?? "0"));
    const qty = parseFloat(String(line.quantity ?? "0"));
    if (!Number.isFinite(unit) || !Number.isFinite(qty)) return total;
    return total + unit * qty;
  }, 0);
  return Math.round(subtotal);
};

/** 対価の内訳を「3 行 / 165,000円（税込）」のような 1 行に畳む。 */
const formatCompensationLines = (lines: ApiCompensationLine[]): string => {
  if (lines.length === 0) return EMPTY_DASH;
  const amount =
    formatOfferAmountWithTax(sumApiCompensationLines(lines), EMPTY_DASH) ??
    EMPTY_DASH;
  return `${lines.length}行 / ${amount}`;
};

/** 1 フィールドぶんの値を表示用テキストにする。 */
export const formatAmendmentValue = (
  field: DmAmendableField,
  value: DmAmendmentChange["previous"],
): string => {
  if (value === null || value === undefined || value === "") return EMPTY_DASH;
  if (isCompensationLines(value)) return formatCompensationLines(value);

  switch (field) {
    case "contract_amount": {
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return EMPTY_DASH;
      return formatOfferAmountWithTax(numeric, EMPTY_DASH) ?? EMPTY_DASH;
    }
    case "contract_start_date":
    case "contract_end_date":
    case "deadline":
      return formatOfferDateTime(String(value), EMPTY_DASH) ?? EMPTY_DASH;
    default:
      return String(value);
  }
};

/** タイムラインの各時点で表示する契約の内容。 */
export interface ContractSnapshot {
  deadline: string | null;
  contract_amount: number | null;
  initial_message: string | null;
}

type AmendmentChanges = DmPendingAmendment["changes"];

/**
 * 「ある時点の契約条件」を、その後に起きた更新の差分から逆算する。
 *
 * タイムラインの契約書アイテムは、提出・更新された **その時点の内容** を表示し
 * 続ける必要がある（後の更新でさかのぼって書き換わってはいけない）。スレッドが
 * 持っているのは常に最新値だけなので、`changes[field].previous`（変更前の値）を
 * 新しい順に辿って巻き戻す。
 *
 * ある時点の値 = その時点より後で**最初に**そのフィールドを変更した更新の
 * `previous`。以降一度も変更されていなければスレッドの現在値。
 *
 * @param laterAmendments その時点より後に成立した更新の差分（時系列順）
 * @param current スレッドの現在値
 */
export const resolveContractSnapshot = (
  laterAmendments: AmendmentChanges[],
  current: ContractSnapshot,
): ContractSnapshot => {
  const rewindText = (
    field: DmAmendableField,
    fallback: string | null,
  ): string | null => {
    for (const changes of laterAmendments) {
      const change = changes?.[field];
      if (!change) continue;
      return typeof change.previous === "string" ? change.previous : null;
    }
    return fallback;
  };

  const rewindAmount = (): number | null => {
    for (const changes of laterAmendments) {
      // 契約金額は `contract_amount` を直接変えた場合と、`compensation_lines`
      // から同期された場合の 2 通りで動く。更新モーダルは対価行を送るので、
      // 実際の差分には `compensation_lines` しか載らないことが多い。
      // 内訳から金額を復元しないと、過去の要点が最新値のままになる。
      const direct = changes?.contract_amount;
      if (direct) {
        if (typeof direct.previous !== "string") return null;
        const numeric = Number(direct.previous);
        return Number.isFinite(numeric) ? numeric : null;
      }
      const lines = changes?.compensation_lines;
      if (lines) {
        if (!Array.isArray(lines.previous)) return null;
        return sumApiCompensationLines(lines.previous);
      }
    }
    return current.contract_amount;
  };

  return {
    deadline: rewindText("deadline", current.deadline),
    contract_amount: rewindAmount(),
    initial_message: rewindText("initial_message", current.initial_message),
  };
};

export interface AmendmentChangeRow {
  field: DmAmendableField;
  label: string;
  previous: string;
  next: string;
}

/**
 * 差分を表示順に並べた行の配列へ変換する。並び順はオファー作成フォームの
 * 項目順（ロール → 条件 → 内容 → 詳細）に合わせる。未知のフィールドが増えた
 * 場合は末尾にフィールド名のまま並べる（BE 先行リリースでも欠落させない）。
 */
export const describeAmendmentChanges = (
  changes: DmPendingAmendment["changes"] | undefined | null,
): AmendmentChangeRow[] => {
  if (!changes) return [];
  const keys = Object.keys(changes) as DmAmendableField[];
  const ordered = [
    ...FIELD_ORDER.filter((field) => field in changes),
    ...keys.filter((field) => !FIELD_ORDER.includes(field)),
  ];
  return ordered.reduce<AmendmentChangeRow[]>((rows, field) => {
    const change = changes[field];
    if (!change) return rows;
    rows.push({
      field,
      label: AMENDMENT_FIELD_LABELS[field] ?? field,
      previous: formatAmendmentValue(field, change.previous),
      next: formatAmendmentValue(field, change.new),
    });
    return rows;
  }, []);
};
